import React, { useState, useEffect, useRef } from 'react';
import { useTransition, animated } from 'react-spring';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async";

import backgroundImage from '../assets/img/coudounat.webp';
import backgroundImage2 from '../assets/img/petillant.webp';
import introImage from '../assets/img/cogna.webp';   // image de la section intro (change-la si tu veux)

/* Images de la frise, associees par annee.
   Change les fichiers librement (ou mets null pour ne pas afficher d'image). */
import tl2001 from '../assets/img/petillant.webp';
import tl2002 from '../assets/img/cogna.webp';
import tl2011 from '../assets/img/coudounat.webp';
import tl2018 from '../assets/img/fleurcogna.webp';
import tl2019 from '../assets/img/chatgptverre.webp';
import tl2024 from '../assets/img/chatgptmanger.webp';

import Button from '../components/Buttons/Button';
import Product from '../components/Cards/Products';
import SVGSeparator from '../services/SVGSeparator';
import InstagramFeed from '../services/InstagramFeed';
import SocialLinks from '../services/SocialLinks';

const quinceImg = '/assets/img/quince.png';

/* Images de la frise, associees par annee.
   Change les fichiers librement (ou mets null pour ne pas afficher d'image). */
const TIMELINE_IMAGES = {
  '2001': tl2001,
  '2002': tl2002,
  '2011': tl2011,
  '2018': tl2018,
  '2019': tl2019,
  '2024': tl2024,
};

/* Lien "Lire plus" optionnel par annee (ou rien). */
const TIMELINE_LINKS = {
  '2024': '/aboutus',
};

/* Petit hexagone (marqueur sur la ligne centrale). Couleur configurable
   pour coller au fond de la frise. */
const Hexagon = ({ fill = '#2E2318' }) => (
  <svg width="22" height="24" viewBox="0 0 22 24" aria-hidden="true">
    <polygon
      points="11,1 20.5,6.5 20.5,17.5 11,23 1.5,17.5 1.5,6.5"
      fill={fill}
      stroke="#D9A641"
      strokeWidth="1.5"
    />
  </svg>
);

/* Apparition au scroll (meme hook que la page About). */
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
};

/* ------------------------------------------------------------------ */
/* Frise chronologique SOMBRE, chapitree par epoques, facon LMDW.     */
/* - chaque epoque a un en-tete (sur-titre + titre + texte)           */
/* - jalons alternes avec ligne centrale + hexagone + filigrane       */
/* - barre de navigation collante en bas avec les epoques cliquables  */
/* ------------------------------------------------------------------ */

const MilestoneRow = ({ item, side, readMoreLabel }) => {
  const [ref, visible] = useReveal();
  const isLeft = side === 'left';
  const image = TIMELINE_IMAGES[item.year];
  const link = TIMELINE_LINKS[item.year];

  return (
    <div ref={ref} className="relative md:grid md:grid-cols-2 md:gap-x-16 mb-14 md:mb-24">
      {/* Hexagone sur la ligne centrale */}
      <div className="hidden md:flex absolute left-1/2 top-1 -translate-x-1/2 z-10 items-center justify-center">
        <Hexagon />
      </div>

      {/* IMAGE (cote oppose au texte) */}
      {image && (
        <div
          className={`mb-6 md:mb-0 transition-all duration-1000 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          } ${isLeft ? 'md:col-start-2 md:row-start-1 md:pl-16' : 'md:col-start-1 md:row-start-1 md:pr-16 md:flex md:justify-end'}`}
        >
          <img
            src={image}
            alt={item.year}
            loading="lazy"
            decoding="async"
            className="w-full md:max-w-sm h-52 md:h-64 object-cover rounded-xl shadow-2xl"
          />
        </div>
      )}

      {/* TEXTE */}
      <div
        className={`transition-all duration-1000 delay-100 ease-out ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        } ${isLeft
          ? 'md:col-start-1 md:row-start-1 md:text-right md:pr-16'
          : 'md:col-start-2 md:row-start-1 md:text-left md:pl-16'}`}
      >
        <div className="font-sans text-4xl md:text-5xl mb-4" style={{ color: '#D9A641' }}>
          {item.year}
        </div>
        <p
          className={`font-nunito text-lg md:text-xl leading-relaxed ${isLeft ? 'md:ml-auto' : 'md:mr-auto'}`}
          style={{ maxWidth: '30rem', color: '#F0EADF' }}
        >
          {item.text}
        </p>
        {link && (
          <Link
            to={link}
            className="inline-block mt-5 font-sans text-sm tracking-[0.25em] uppercase border-b pb-1 transition-opacity hover:opacity-70"
            style={{ color: '#D9A641', borderColor: '#D9A641' }}
          >
            {readMoreLabel}
          </Link>
        )}
      </div>
    </div>
  );
};

const EraSection = ({ era, readMoreLabel, innerRef, dataIdx }) => {
  const [headRef, headVisible] = useReveal();
  const items = Array.isArray(era.items) ? era.items : [];

  return (
    <div
      ref={innerRef}
      data-idx={dataIdx}
      className="relative px-6 py-20 md:py-28 scroll-mt-24"
    >
      {/* En-tete de l'epoque */}
      <div
        ref={headRef}
        className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ease-out ${
          headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <span className="text-xs md:text-sm font-semibold tracking-[0.35em] uppercase" style={{ color: '#D9A641' }}>
          {era.kicker}
        </span>
        <h3 className="font-sans mt-5 text-3xl md:text-5xl leading-tight" style={{ color: '#F7F2E8' }}>
          {era.title}
        </h3>
        {era.text && (
          <p className="mt-5 font-nunito text-base md:text-lg" style={{ color: 'rgba(247,242,232,0.55)' }}>
            {era.text}
          </p>
        )}
      </div>

      {/* Jalons */}
      <div className="relative max-w-5xl mx-auto">
        <span
          className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px"
          style={{ backgroundColor: 'rgba(217,166,65,0.45)' }}
          aria-hidden="true"
        />
        <span
          className="md:hidden absolute left-1.5 top-0 bottom-0 w-px"
          style={{ backgroundColor: 'rgba(217,166,65,0.45)' }}
          aria-hidden="true"
        />
        <div className="md:pl-0 pl-8">
          {items.map((item, i) => (
            <MilestoneRow
              key={item.year || i}
              item={item}
              side={i % 2 === 0 ? 'left' : 'right'}
              readMoreLabel={readMoreLabel}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const HistoryTimeline = () => {
  const { t } = useTranslation();
  const [introRef, introVisible] = useReveal();

  const rawEras = t('timeline.eras', { returnObjects: true });
  const eras = Array.isArray(rawEras) ? rawEras : [];
  const readMoreLabel = t('timeline.readMore');

  const [active, setActive] = useState(0);
  const eraRefs = useRef([]);

  // Scroll spy : met en surbrillance l'epoque visible dans la barre du bas.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(Number(entry.target.dataset.idx));
          }
        });
      },
      { rootMargin: '-40% 0px -40% 0px' }
    );
    eraRefs.current.forEach((n) => n && observer.observe(n));
    return () => observer.disconnect();
  }, [eras.length]);

  const goTo = (i) => {
    eraRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="histoire" className="relative overflow-hidden" style={{ backgroundColor: '#2E2318' }}>
      {/* Coings sur les COTES uniquement (silhouettes claires, discretes) */}
      <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
        className="hidden md:block absolute top-28 left-4 w-28 pointer-events-none select-none"
        style={{ opacity: 0.05, filter: 'brightness(0) invert(1)' }} />
      <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
        className="hidden md:block absolute top-[28%] right-6 w-32 pointer-events-none select-none"
        style={{ opacity: 0.05, filter: 'brightness(0) invert(1)' }} />
      <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
        className="hidden md:block absolute top-[52%] left-6 w-24 pointer-events-none select-none"
        style={{ opacity: 0.05, filter: 'brightness(0) invert(1)' }} />
      <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
        className="hidden md:block absolute top-[74%] right-4 w-28 pointer-events-none select-none"
        style={{ opacity: 0.05, filter: 'brightness(0) invert(1)' }} />
      <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
        className="hidden md:block absolute bottom-28 left-8 w-24 pointer-events-none select-none"
        style={{ opacity: 0.05, filter: 'brightness(0) invert(1)' }} />

      {/* Intro facon "NOTRE HISTOIRE" */}
      <div
        ref={introRef}
        className={`text-center px-6 pt-28 pb-10 transition-all duration-1000 ease-out ${
          introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <span className="text-xs md:text-sm font-semibold tracking-[0.4em] uppercase" style={{ color: '#D9A641' }}>
          {t('timeline.kicker')}
        </span>
        <h2 className="font-sans mt-6 text-5xl md:text-7xl leading-none">
          {/* Premier mot en contour, second plein */}
          <span
            className="block"
            style={{ color: 'transparent', WebkitTextStroke: '1px rgba(247,242,232,0.8)' }}
          >
            {t('timeline.title1')}
          </span>
          <span className="block" style={{ color: '#F7F2E8' }}>
            {t('timeline.title2')}
          </span>
        </h2>
        <p className="mt-8 mx-auto max-w-2xl font-nunito text-base md:text-lg" style={{ color: 'rgba(247,242,232,0.6)' }}>
          {t('timeline.intro')}
        </p>
        <div className="mt-10 flex justify-center">
          <span className="block w-px h-12" style={{ backgroundColor: 'rgba(217,166,65,0.6)' }} />
        </div>
      </div>

      {/* Epoques */}
      {eras.map((era, i) => (
        <EraSection
          key={i}
          era={era}
          readMoreLabel={readMoreLabel}
          dataIdx={i}
          innerRef={(el) => { eraRefs.current[i] = el; }}
        />
      ))}

      {/* Barre de navigation collante en bas */}
      <div
        className="sticky bottom-0 z-20 border-t backdrop-blur"
        style={{ backgroundColor: 'rgba(46,35,24,0.92)', borderColor: 'rgba(217,166,65,0.3)' }}
      >
        <div className="max-w-6xl mx-auto flex justify-center gap-4 md:gap-10 px-4 py-4 overflow-x-auto">
          {eras.map((era, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="font-sans whitespace-nowrap text-xs md:text-sm tracking-[0.15em] uppercase transition-colors duration-300 pb-1 border-b-2"
              style={{
                color: active === i ? '#D9A641' : 'rgba(247,242,232,0.5)',
                borderColor: active === i ? '#D9A641' : 'transparent',
              }}
            >
              {era.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const slides = [
    { id: 1, url: backgroundImage },
    { id: 2, url: backgroundImage2 },
  ];

  const [index, setIndex] = useState(0);

  const transitions = useTransition(index, {
    from: { opacity: 0, transform: 'scale(1.08)' },
    enter: { opacity: 1, transform: 'scale(1)' },
    leave: { opacity: 0, transform: 'scale(1)' },
    config: { tension: 280, friction: 30, duration: 1200 },
    keys: index,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(current => (current + 1) % slides.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const getBackgroundPosition = (currentUrl) =>
    currentUrl.includes('coudounat') ? '90% 25%' : '5% 18%';

  const [introRef, introVisible] = useReveal();
  const [productsRef, productsVisible] = useReveal();

  return (
    <div style={{ backgroundColor: '#FBF7EF' }}>
      <Helmet>
        <title>Au Coing du Ventoux | Des produits a base de Coing</title>
        <meta name="description" content="Decouvrez nos alcool de coing artisanaux ainsi que leurs produits derives, fabrique au pied du Mont Ventoux. Livraison rapide et directe du producteur." />
        <meta name="keywords" content="alcool de coing, liqueur de coing, eau de vie, boutique artisanal, Mont Ventoux" />
        <meta property="og:title" content="Au Coing du Ventoux | Des produits a base de Coing" />
        <meta property="og:description" content="Decouvrez nos alcool de coing artisanaux ainsi que leurs produits derives, fabrique au pied du Mont Ventoux. Livraison rapide et directe du producteur." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aucoingduventoux.com/" />
        <meta property="og:image" content="https://aucoingduventoux.com/images/bouteille-coing.jpg" />
        <link rel="canonical" href="https://aucoingduventoux.com/" />
      </Helmet>

      {/* ============================= HERO ============================= */}
      <header className="relative h-screen min-h-[600px] overflow-hidden">
        {transitions((styles, i) => (
          <animated.div
            key={i}
            style={{
              ...styles,
              backgroundImage: `url(${slides[i].url})`,
              position: 'absolute',
              inset: 0,
              backgroundPosition: getBackgroundPosition(slides[i].url),
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              willChange: 'opacity, transform',
            }}
          />
        ))}

        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(30,20,8,0.6) 0%, rgba(30,20,8,0.45) 45%, rgba(30,20,8,0.7) 100%)' }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 60%)' }}
        />

        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <h1
            className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight max-w-4xl"
            style={{ textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}
          >
            {t('home.title')}
          </h1>
          <p
            className="mt-6 font-nunito text-lg md:text-xl lg:text-2xl max-w-2xl text-stone-100/90"
            style={{ textShadow: '0 1px 10px rgba(0,0,0,0.6)' }}
          >
            {t('home.description')}
          </p>

          <button
            onClick={() => navigate('/aboutus')}
            className="font-sans mt-10 px-9 py-3.5 rounded-full text-stone-900 bg-white text-base md:text-lg transition-transform duration-300 hover:scale-105 shadow-lg"
          >
            {t('home.button.discover')}
          </button>

          <div className="absolute bottom-10 left-0 right-0 flex justify-center gap-3">
            {slides.map((slide, slideIndex) => (
              <button
                key={slide.id}
                onClick={() => setIndex(slideIndex)}
                aria-label={`Voir l'image ${slideIndex + 1}`}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  index === slideIndex ? 'w-8 bg-white' : 'w-2.5 bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* ===================== SECTION INTRO (image + texte court) ===================== */}
      <section
        ref={introRef}
        className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 overflow-hidden"
      >
        <img
          src={quinceImg} alt="" aria-hidden="true" loading="lazy" width={128} height={128}
          className="absolute top-10 -right-10 w-32 opacity-[0.06] pointer-events-none"
        />

        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div
            className={`lg:w-1/2 transition-all duration-1000 ease-out ${
              introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-800">
              Soleu &amp; Destre
            </span>
            <h2 className="font-sans mt-6 text-2xl md:text-3xl lg:text-4xl leading-snug text-stone-800">
              <Trans i18nKey="home.section1.title" />
            </h2>
            <p className="mt-8 font-nunito text-lg leading-relaxed text-stone-600">
              {t('home.section1.description')}
            </p>
            <button
              onClick={() => navigate('/aboutus')}
              className="font-sans mt-8 inline-flex items-center gap-2 text-amber-800 hover:gap-3 transition-all duration-300"
            >
              En savoir plus <span aria-hidden="true">&rarr;</span>
            </button>
          </div>

          <div
            className={`lg:w-1/2 w-full transition-all duration-1000 delay-150 ease-out ${
              introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <div className="relative">
              <div
                className="absolute -inset-3 rounded-2xl rotate-[1.5deg]"
                style={{ backgroundColor: '#F0E6D2' }}
                aria-hidden="true"
              />
              <img
                src={introImage}
                alt="Le coing, au pied du Mont Ventoux"
                loading="lazy"
                decoding="async"
                className="relative w-full h-[320px] md:h-[460px] object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== SECTION PRODUITS ===================== */}
      <section ref={productsRef} className="relative py-20 md:py-28" style={{ backgroundColor: '#F4ECDD' }}>
        <div
          className={`text-center px-6 transition-all duration-1000 ease-out ${
            productsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-800">
            Notre savoir-faire
          </span>
          <h2 className="font-sans mt-5 text-3xl md:text-4xl lg:text-5xl text-stone-800">
            <Trans i18nKey="home.section2.title" />
          </h2>
          <SVGSeparator width={120} height={50} className="mx-auto" />
          <p className="mt-4 mx-auto max-w-2xl font-nunito text-lg text-stone-600">
            {t('home.section2.description')}
          </p>
        </div>

        <div className="mt-12 px-4 md:px-10">
          <Product />
        </div>

        <div className="text-center mt-12">
          <Button text={t('home.button.shop')} onClick={() => navigate('/boutique')} variant="black" />
        </div>
      </section>

      {/* ===================== FRISE CHRONOLOGIQUE (sombre, chapitree) ===================== */}
      <HistoryTimeline />

      {/* ===================== INSTAGRAM ===================== */}
      <section className="px-5 md:px-10 py-16" style={{ backgroundColor: '#F4ECDD' }}>
        <InstagramFeed />
      </section>

      {/* ===================== RESEAUX SOCIAUX ===================== */}
      <SocialLinks />
    </div>
  );
};